import { auth } from '@/lib/firebase'
import { useQueryClient } from '@tanstack/react-query'
import {
  onIdTokenChanged,
  sendEmailVerification,
  type User,
} from 'firebase/auth'
import { createContext, useEffect, useState } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import * as Sentry from '@sentry/react'

export const AuthContext = createContext<{
  authState: User | null | undefined
  loading: boolean
  error: Error | null | undefined
  idToken: string
}>({ authState: null, loading: true, error: null, idToken: undefined!! })
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient()
  const [authState, loading, error] = useAuthState(auth, {
    onUserChanged: async (user) => {
      if (user) {
        Sentry.setUser({
          id: user.uid,
          email: user.email || undefined,
          name: user.displayName || undefined,
        })
        if (!user.emailVerified) {
          sendEmailVerification(user)
        }
      } else {
        Sentry.setUser(null)
      }
      queryClient.invalidateQueries()
    },
  })

  const [idToken, setIdToken] = useState<string | undefined>()
  useEffect(() => {
    return onIdTokenChanged(auth, async (user) => {
      setIdToken(user ? await user.getIdToken() : undefined)
    })
  }, [])

  return (
    <AuthContext.Provider
      value={{
        authState,
        loading,
        error,
        idToken: idToken!!,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
